import { type ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useIsAdmin } from "../lib/useIsAdmin";
import { useProjects, notifyProjectsChanged } from "../lib/useProjects";
import { ProjectDialog } from "./ProjectDialog";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { Avatar } from "../ui/Avatar";
import { Icon } from "../ui/icons";
import { applyTheme, type Theme } from "../ui/theme";
import { t } from "../i18n/index";
import { getDefaultWorkspace } from "../lib/workspace";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIDEBAR_W = 248;
const RAIL_W = 56;
const MOBILE_BP = 768;    // < 768px → drawer mode
const RAIL_BP = 1024;     // 768–1024px → icon rail
const PROJECTS_EXPANDED_KEY = "timlul-nav-projects";

// ---------------------------------------------------------------------------
// NavItem — a single sidebar link with optional icon
// ---------------------------------------------------------------------------

interface NavItemProps {
  to: string;
  icon: Parameters<typeof Icon>[0]["name"];
  label: string;
  end?: boolean;
  /** If true the item has been activated (desktop collapsed / mobile rail) */
  iconOnly?: boolean;
  onClick?: () => void;
}

function NavItem({ to, icon, label, end = false, iconOnly = false, onClick }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        `sidebar-nav-item${isActive ? " sidebar-nav-item--active" : ""}`
      }
      title={iconOnly ? label : undefined}
    >
      <Icon name={icon} size={18} aria-hidden />
      {!iconOnly && <span className="sidebar-nav-label">{label}</span>}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// SidebarContent — the full sidebar tree (shared between desktop + drawer)
// ---------------------------------------------------------------------------

interface SidebarContentProps {
  session: Session;
  iconOnly: boolean;
  onNav?: () => void;
}

function SidebarContent({ session, iconOnly, onNav }: SidebarContentProps) {
  const { isAdmin } = useIsAdmin();
  const location = useLocation();
  const routeKey = location.pathname + location.search;
  const { projects, unassignedCount } = useProjects(routeKey);
  const navigate = useNavigate();

  // Sidebar "Add project" dialog state
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  // Fetch workspace id lazily (only when the user opens the dialog)
  function openAddProject() {
    setAddProjectOpen(true);
    if (!workspaceId) {
      getDefaultWorkspace()
        .then((ws) => setWorkspaceId(ws.id))
        .catch(() => {/* silent — ProjectDialog shows the error */});
    }
  }

  // Projects expand/collapse — persist to localStorage
  const [projectsOpen, setProjectsOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PROJECTS_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });

  function toggleProjects(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setProjectsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PROJECTS_EXPANDED_KEY, String(next));
      } catch {}
      return next;
    });
  }

  // Theme state — read from html dataset
  const [theme, setTheme] = useState<Theme>(() => {
    return (document.documentElement.dataset.theme as Theme) ?? "dark";
  });

  function handleThemeToggle() {
    const next: Theme = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    setTheme(next);
  }

  const email = session.user.email ?? "";

  return (
    <div className="sidebar-inner">
      {/* ---- Brand ---- */}
      <div className="sidebar-brand">
        <div className="sidebar-brand-mark" aria-hidden>
          {/* Simple "T" monogram mark */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <text x="50%" y="76%" dominantBaseline="auto" textAnchor="middle" fill="currentColor" fontSize="12" fontWeight="700" fontFamily="Heebo, sans-serif">T</text>
          </svg>
        </div>
        {!iconOnly && (
          <span className="sidebar-brand-name">
            Timlul <span className="sidebar-brand-he">· תמלול</span>
          </span>
        )}
      </div>

      {/* ---- Primary CTA ---- */}
      {iconOnly ? (
        <IconButton
          label={t("nav.newTranscript")}
          name="upload"
          size={18}
          className="sidebar-cta-icon"
          onClick={() => {
            navigate("/meetings/new");
            onNav?.();
          }}
          title={t("nav.newTranscript")}
        />
      ) : (
        <Button
          variant="primary"
          icon="upload"
          className="sidebar-cta"
          onClick={() => {
            navigate("/meetings/new");
            onNav?.();
          }}
        >
          {t("nav.newTranscript")}
        </Button>
      )}

      {/* ---- Primary navigation ---- */}
      <nav className="sidebar-nav" aria-label="Main navigation">
        <NavItem
          to="/"
          end
          icon="inbox"
          label={t("nav.library")}
          iconOnly={iconOnly}
          onClick={onNav}
        />

        {/* Projects with expand toggle */}
        <div className="sidebar-projects-group">
          <NavLink
            to="/projects"
            className={({ isActive }) =>
              `sidebar-nav-item${isActive ? " sidebar-nav-item--active" : ""}`
            }
            title={iconOnly ? t("nav.projects") : undefined}
            onClick={onNav}
          >
            <Icon name="folder" size={18} aria-hidden />
            {!iconOnly && (
              <>
                <span className="sidebar-nav-label">{t("nav.projects")}</span>
                <button
                  type="button"
                  className="sidebar-projects-chevron"
                  aria-label={projectsOpen ? "Collapse projects" : "Expand projects"}
                  onClick={toggleProjects}
                  data-testid="projects-expand"
                >
                  <Icon
                    name={projectsOpen ? "chevron-up" : "chevron-down"}
                    size={14}
                    aria-hidden
                  />
                </button>
                {/* "+" quick-create button — visible on hover */}
                <IconButton
                  label={t("nav.addProject")}
                  name="plus"
                  size={14}
                  className="sidebar-projects-add"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openAddProject();
                  }}
                  title={t("nav.addProject")}
                  data-testid="sidebar-add-project"
                />
              </>
            )}
          </NavLink>

          {/* Project sub-links */}
          {!iconOnly && projectsOpen && (projects.length > 0 || unassignedCount > 0) && (
            <div className="sidebar-projects-sub">
              {/* "Unassigned" entry — shown only when there are unassigned meetings */}
              {unassignedCount > 0 && (
                <NavLink
                  to="/?project=unassigned"
                  className={({ isActive }) =>
                    `sidebar-nav-sub-item sidebar-nav-sub-item--unassigned${isActive ? " sidebar-nav-sub-item--active" : ""}`
                  }
                  onClick={onNav}
                >
                  <Icon name="inbox" size={12} aria-hidden style={{ opacity: 0.6, flexShrink: 0 }} />
                  <span>{t("nav.unassigned")} ({unassignedCount})</span>
                </NavLink>
              )}
              {projects.map((p) => (
                <NavLink
                  key={p.id}
                  to={`/projects/${p.id}`}
                  dir="auto"
                  className={({ isActive }) =>
                    `sidebar-nav-sub-item${isActive ? " sidebar-nav-sub-item--active" : ""}`
                  }
                  onClick={onNav}
                >
                  {p.name}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* ---- Footer section ---- */}
      <div className="sidebar-footer">
        <NavItem
          to="/help"
          icon="help"
          label={t("nav.help")}
          iconOnly={iconOnly}
          onClick={onNav}
        />
        {isAdmin && (
          <NavItem
            to="/admin"
            icon="shield"
            label={t("nav.admin")}
            iconOnly={iconOnly}
            onClick={onNav}
          />
        )}

        <div className="sidebar-divider" role="separator" />

        {/* Theme toggle */}
        <IconButton
          label="Toggle theme"
          name={theme === "dark" ? "sun" : "moon"}
          size={18}
          onClick={handleThemeToggle}
          className="sidebar-theme-toggle"
          title="Toggle theme"
        />

        {/* User row */}
        {!iconOnly ? (
          <div className="sidebar-user-row">
            <Avatar name={email} size={28} />
            <span className="sidebar-user-email" title={email}>
              {email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="sidebar-signout"
              onClick={() => supabase.auth.signOut()}
              aria-label="Sign out"
            >
              {t("nav.signOut")}
            </Button>
          </div>
        ) : (
          <div className="sidebar-user-row sidebar-user-row--icon">
            <Avatar name={email} size={28} />
            <Button
              variant="ghost"
              size="sm"
              className="sidebar-signout"
              onClick={() => supabase.auth.signOut()}
              aria-label="Sign out"
            />
          </div>
        )}
      </div>

      {/* Sidebar add-project dialog */}
      <ProjectDialog
        open={addProjectOpen}
        mode="create"
        workspaceId={workspaceId}
        onClose={() => setAddProjectOpen(false)}
        onSaved={(id) => {
          setAddProjectOpen(false);
          notifyProjectsChanged();
          navigate(`/projects/${id}`);
          onNav?.();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AppShell — responsive wrapper
// ---------------------------------------------------------------------------

export function AppShell({ session, children }: { session: Session; children: ReactNode }) {
  // isMobile: < 768px → hamburger + drawer
  // isRail: 768–1024px → 56px icon rail
  // isDesktop: >= 1024px → full 248px sidebar
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BP);
  const [isRail, setIsRail] = useState(
    () => window.innerWidth >= MOBILE_BP && window.innerWidth < RAIL_BP,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      setIsMobile(w < MOBILE_BP);
      setIsRail(w >= MOBILE_BP && w < RAIL_BP);
      if (w >= MOBILE_BP) setDrawerOpen(false);
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const iconOnly = isRail && !isMobile;

  return (
    <div className={`app app--sidebar${isMobile ? " app--mobile" : ""}`}>
      {/* ---- Desktop / rail sidebar ---- */}
      {!isMobile && (
        <aside
          className={`sidebar${iconOnly ? " sidebar--rail" : ""}`}
          style={{ width: iconOnly ? RAIL_W : SIDEBAR_W }}
          aria-label="Sidebar navigation"
        >
          <SidebarContent session={session} iconOnly={iconOnly} />
        </aside>
      )}

      {/* ---- Mobile: fixed top bar ---- */}
      {isMobile && (
        <header className="mobile-topbar">
          <IconButton
            label="Open navigation menu"
            name="menu"
            size={20}
            onClick={() => setDrawerOpen(true)}
          />
          <span className="mobile-brand">Timlul</span>
        </header>
      )}

      {/* ---- Mobile drawer ---- */}
      {isMobile && drawerOpen && (
        <>
          {/* Overlay */}
          <div
            className="sidebar-overlay"
            aria-hidden
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer */}
          <aside className="sidebar sidebar--drawer" aria-label="Sidebar navigation">
            <SidebarContent
              session={session}
              iconOnly={false}
              onNav={() => setDrawerOpen(false)}
            />
          </aside>
        </>
      )}

      {/* ---- Main content ---- */}
      <main className="page">{children}</main>
    </div>
  );
}
